import React, { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../firebase';
import { COLOR_OPTIONS } from '../constants';
import InstallAppButton from './InstallAppButton';
import { VERIFICATION_CONTINUE_URL } from '../verification';

// A small preview of the core two-orb mechanic, used as the sign-in hero.
// The two orbs pulse out of phase (right one delayed half a cycle) to evoke the
// left↔right bilateral rhythm. Reuses .orb-pulse + the orb gradients/glows.
const Orb = ({ color, delay = '0s', size = 50 }) => (
  <span
    className="orb-pulse rounded-full"
    style={{
      width: size,
      height: size,
      background: COLOR_OPTIONS[color].gradient,
      boxShadow: `0 0 28px 6px ${COLOR_OPTIONS[color].shadow}`,
      animationDelay: delay,
    }}
  />
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3 3l18 18" />
    <path d="M10.6 5.1A10.9 10.9 0 0 1 12 5c7 0 10.5 7 10.5 7a13.9 13.9 0 0 1-3.15 4.05M6.5 6.5C3.4 8.4 1.5 12 1.5 12S5 19 12 19a10.8 10.8 0 0 0 3.5-.58" />
    <path d="M9.5 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

const BrandHero = () => (
  <div className="flex flex-col items-center mb-8 select-none">
    <div className="flex items-center gap-4 mb-5">
      <Orb color="blue" />
      <Orb color="purple" />
    </div>
    <h1 className="text-5xl font-extrabold tracking-tight bg-gradient-to-r from-blue-400 via-indigo-300 to-purple-400 bg-clip-text text-transparent">
      TapTap
    </h1>
    <p className="text-breath text-gray-400 text-sm mt-2 tracking-wide">Tap. Breathe. Relax.</p>
  </div>
);

const AuthForm = ({ onLogin, onGuest }) => {
  const [view, setView] = useState('login'); // 'login', 'signup', or 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setSending(true);

    try {
      if (view === 'login') {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        onLogin(userCredential.user);
      } else if (view === 'signup') {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // The verification gate keeps this account outside the app until verified.
        // Failed delivery can be retried from that screen.
        try { await sendEmailVerification(userCredential.user, { url: VERIFICATION_CONTINUE_URL, handleCodeInApp: false }); } catch { /* ignore */ }
        onLogin(userCredential.user);
      } else if (view === 'reset') {
        // Enumeration-safe: always show the same message whether or not the
        // email has an account, and swallow user-not-found below.
        await sendPasswordResetEmail(auth, email.trim(), {
          url: 'https://app.taptap.org.uk/',
          handleCodeInApp: false,
        });
        setMessage({ type: 'success', text: 'Check your inbox and spam folder. If an account exists for that email, Firebase has sent a password-reset link.' });
      }
    } catch (error) {
      // Don't leak which emails exist or whether the password was wrong.
      if (view === 'reset' && error.code === 'auth/user-not-found') {
        setMessage({ type: 'success', text: 'Check your inbox and spam folder. If an account exists for that email, Firebase has sent a password-reset link.' });
        return;
      }
      // Specific, friendly messages where they don't leak account existence on
      // login (wrong-password / user-not-found stay generic below). Signup's
      // email-already-in-use is surfaced because it's the standard, expected UX.
      const messages = {
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/weak-password': 'Please choose a stronger password (at least 6 characters).',
        'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
        'auth/network-request-failed': 'No connection — please check your internet and try again.',
        'auth/email-already-in-use': 'That email already has an account — please log in instead.',
        'auth/unauthorized-continue-uri': 'The password-reset return address is not authorised yet. Please contact TapTap support.',
        'auth/missing-continue-uri': 'The password-reset return address is missing. Please contact TapTap support.',
      };
      setMessage({
        type: 'error',
        text: messages[error.code]
          || (view === 'signup'
            ? 'Could not create your account. Please try again.'
            : view === 'reset'
              ? 'The reset email could not be sent. Please try again or contact TapTap support.'
              : 'Incorrect email or password.'),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-b from-slate-950 via-indigo-950/40 to-slate-950">
      <div className="w-full max-w-md">
        <BrandHero />

        <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl shadow-2xl w-full border border-white/15">
          <h2 className="text-2xl font-bold mb-6 text-center text-white">
            {view === 'login' ? 'Welcome back' : view === 'signup' ? 'Create your account' : 'Reset password'}
          </h2>

          {message.text && (
            <div role={message.type === 'error' ? 'alert' : 'status'} className={`mb-4 p-3 rounded-xl text-sm text-center ${message.type === 'error' ? 'bg-red-500/20 text-red-200' : 'bg-green-500/20 text-green-200'}`}>
              {message.text}
            </div>
          )}

          <label htmlFor="auth-email" className="mb-2 block text-sm text-slate-300">Email</label>
          <div className="relative mb-4">
            <input
              id="auth-email" autoComplete="email" name="email" type="email"
              placeholder="Email"
              className="w-full p-3 pr-16 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 outline-none transition-colors focus:border-purple-400/70 focus:bg-white/10"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            {email && (
              <button
                type="button"
                onClick={() => setEmail('')}
                aria-label="Clear email"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 active:scale-90 transition-all"
              >
                <span className="text-sm leading-none">✕</span>
              </button>
            )}
          </div>

          {view !== 'reset' && (
            <div className="mb-6">
              <label htmlFor="auth-password" className="mb-2 block text-sm text-slate-300">Password</label>
              <div className="relative">
              <input
                id="auth-password" name="password" autoComplete={view === 'signup' ? 'new-password' : 'current-password'}
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                className="w-full p-3 pr-28 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 outline-none transition-colors focus:border-purple-400/70 focus:bg-white/10"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="w-11 h-11 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 active:scale-90 transition-all"
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
                {password && (
                  <button
                    type="button"
                    onClick={() => setPassword('')}
                    aria-label="Clear password"
                    className="w-11 h-11 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 active:scale-90 transition-all"
                  >
                    <span className="text-sm leading-none">✕</span>
                  </button>
                )}
              </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={sending}
            className="w-full p-3 rounded-xl font-bold mb-4 text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 transition-all transform active:scale-95 shadow-lg shadow-purple-500/25"
          >
            {sending ? 'Please wait…' : view === 'login' ? 'Sign In' : view === 'signup' ? 'Create Account' : 'Send Reset Link'}
          </button>

          <div className="space-y-2 text-center">
            {view === 'login' ? (
              <>
                <button type="button" onClick={() => setView('signup')} className="block min-h-11 w-full text-indigo-300 hover:text-indigo-200 text-sm transition-colors">Need an account? Sign Up</button>
                <button type="button" onClick={() => setView('reset')} className="block min-h-11 w-full text-gray-400 hover:text-gray-300 text-xs transition-colors">Forgot your password?</button>
              </>
            ) : (
              <button type="button" onClick={() => setView('login')} className="block min-h-11 w-full text-indigo-300 hover:text-indigo-200 text-sm transition-colors">Back to Login</button>
            )}
          </div>
        </form>
        {onGuest && <button type="button" onClick={onGuest} className="mt-4 min-h-11 w-full rounded-xl border border-indigo-300/25 bg-indigo-400/10 px-4 text-sm font-semibold text-indigo-100">Try Free Tap without an account</button>}
        <p className="mt-4 text-center text-xs text-slate-300">
          How TapTap uses your data: <a href="https://taptap.org.uk/privacy/" target="_blank" rel="noopener noreferrer" className="text-indigo-200 underline">Privacy Policy</a>
        </p>
        <InstallAppButton />
      </div>
    </div>
  );
};

export default AuthForm;
